import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQ = () => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQ();
  }, []);

  const fetchFAQ = async () => {
    try {
      const response = await fetch(`${API_URL}/api/faq`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch FAQ');
      }
      
      const data = await response.json();
      const parsedFAQs = parseFAQContent(data.content || '');
      setFaqs(parsedFAQs);
    } catch (err) {
      console.error('Error fetching FAQ:', err);
      // Fallback to empty array - the API will seed defaults
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  // Parse content into Q&A pairs
  const parseFAQContent = (text) => {
    if (!text) return [];
    
    const blocks = text.split(/\n\s*\n/);
    return blocks
      .filter(block => block.trim())
      .map(block => {
        const lines = block.trim().split('\n');
        const question = lines[0] || '';
        const answer = lines.slice(1).join('\n') || '';
        return { question, answer };
      })
      .filter(faq => faq.question && faq.answer);
  };

  const handleContactSupport = () => {
    navigate('/support');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex justify-center items-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
          <span className="ml-3 text-gray-600">Loading FAQs...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Frequently Asked Questions</h1>
            <p className="text-gray-600">Find answers to common questions about yiBooking</p>
          </div>
          
          {/* FAQ List */}
          {faqs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No FAQs available at the moment.
            </div>
          ) : (
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-200"
                  data-testid={`faq-item-${index}`}
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* Contact Support Section */}
          <div className="mt-12 text-center bg-yellow-50 rounded-lg p-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Still have questions?</h3>
            <p className="text-gray-600 mb-6">Our support team is here to help you get started.</p>
            <button 
              onClick={handleContactSupport}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-8 py-3 rounded-md transition-colors"
              data-testid="contact-support-btn"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FAQ;
